import { useEffect, useRef } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import styles from './FeedComposer.module.scss';

export type Visibility = 'public' | 'followers' | 'private';

export interface FeedComposerExpandedProps {
  register: UseFormRegister<{ body: string }>;
  body: string;
  visibility: Visibility;
  onVisibilityChange: (v: Visibility) => void;
  onSubmit: () => void;
  onClickOutside: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  error: string | null;
  maxLength: number;
}

export default function FeedComposerExpanded({
  register,
  body,
  visibility,
  onVisibilityChange,
  onSubmit,
  onClickOutside,
  isSubmitting,
  canSubmit,
  error,
  maxLength,
}: FeedComposerExpandedProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus textarea on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Click-outside detection
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        if (body.trim().length === 0) {
          onClickOutside();
        }
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [body, onClickOutside]);

  // Keyboard shortcut: Ctrl+Enter / Cmd+Enter to submit
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  }

  const charCount = body.length;

  function getCounterClassName() {
    const classes = [styles.charCounter];
    if (charCount >= 1900 && charCount <= 1999) {
      classes.push(styles.warning);
    }
    if (charCount === 2000) {
      classes.push(styles.atLimit);
    }
    return classes.join(' ');
  }

  // Merge react-hook-form's register ref with our local ref
  const { ref: registerRef, ...registerRest } = register('body');

  return (
    <div className={styles.expanded} ref={wrapperRef}>
      <textarea
        {...registerRest}
        ref={(el) => {
          registerRef(el);
          textareaRef.current = el;
        }}
        rows={3}
        maxLength={maxLength}
        aria-label="Write a new post"
        aria-describedby="char-counter"
        className={styles.textarea}
        onKeyDown={handleKeyDown}
      />

      <div className={styles.actionBar}>
        <select
          className={styles.visibilitySelector}
          value={visibility}
          onChange={(e) => onVisibilityChange(e.target.value as Visibility)}
          aria-label="Post visibility"
        >
          <option value="public">Public</option>
          <option value="followers">Followers</option>
          <option value="private">Private</option>
        </select>

        <span id="char-counter" className={getCounterClassName()}>
          {charCount}/{maxLength}
        </span>

        <button
          type="button"
          className={styles.submitBtn}
          disabled={!canSubmit || isSubmitting}
          onClick={onSubmit}
        >
          {isSubmitting ? (
            <span className={styles.loadingIndicator} aria-hidden="true" />
          ) : null}
          {isSubmitting ? 'Posting...' : 'Post'}
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage} role="alert" aria-live="assertive">
          {error}
        </div>
      )}
    </div>
  );
}
